// Transferencias entre cuentas — crea las dos patas (débito+crédito) vía RPC.
// Control Bancario → Movimientos → Transferencias entre cuentas.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Loader2, Repeat } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, CuentaMini, Modal, cb, cbRpc, fieldCls, hoyISO, money } from './shared';

interface Transferencia {
  transferencia_id: string; fecha: string; concepto: string | null; referencia: string | null;
  origen_alias: string | null; origen_numero: string; monto_origen: number; origen_moneda: string;
  destino_alias: string | null; destino_numero: string; monto_destino: number | null; destino_moneda: string | null;
}

const vacio = { origen: '', destino: '', monto: 0, monto_destino: 0, fecha: hoyISO(), concepto: '', referencia: '' };

export function Transferencias() {
  const qc = useQueryClient();
  const { data: cuentas = [] } = useQuery<CuentaMini[]>({
    queryKey: ['cb-cuentas-mini'],
    queryFn: () => cb.list<CuentaMini>('cuentas', '?select=id,alias,numero,moneda,banco:banco_id(nombre)&deleted_at=is.null&es_activo=is.true&order=alias,numero'),
  });
  const { data: transf = [], isFetching } = useQuery<Transferencia[]>({
    queryKey: ['cb-transferencias'],
    queryFn: () => cbRpc<Transferencia[]>('cb_transferencias'),
  });

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const mOrigen = cuentas.find((c) => c.id === form.origen)?.moneda;
  const mDestino = cuentas.find((c) => c.id === form.destino)?.moneda;
  const distintaMoneda = !!mOrigen && !!mDestino && mOrigen !== mDestino;

  const abrir = () => { setForm({ ...vacio, origen: cuentas[0]?.id ?? '', destino: cuentas[1]?.id ?? '' }); setAbierto(true); };

  const transferir = useMutation({
    mutationFn: () => cbRpc('cb_transferir', {
      _cuenta_origen: form.origen, _cuenta_destino: form.destino, _monto: Number(form.monto),
      _fecha: form.fecha, _concepto: form.concepto || null, _referencia: form.referencia || null,
      _monto_destino: distintaMoneda ? Number(form.monto_destino) : null,
    }),
    onSuccess: () => {
      toast.success('Transferencia registrada');
      setAbierto(false);
      void qc.invalidateQueries({ queryKey: ['cb-transferencias'] });
      void qc.invalidateQueries({ queryKey: ['cb-libro'] });
      void qc.invalidateQueries({ queryKey: ['cb-posicion'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Repeat className="w-5 h-5" /> Transferencias entre cuentas</h1>
        <Button onClick={abrir} disabled={cuentas.length < 2}><Repeat className="w-4 h-4 mr-1" /> Nueva transferencia</Button>
      </div>
      {cuentas.length < 2 && <p className="text-sm text-muted-foreground">Necesitas al menos dos cuentas activas para transferir.</p>}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Historial ({transf.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Origen</th><th className="py-2 pr-3"></th><th className="py-2 pr-3">Destino</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Concepto</th><th className="py-2 pr-3">Ref.</th>
            </tr></thead>
            <tbody>
              {transf.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin transferencias</td></tr>}
              {transf.map((t) => (
                <tr key={t.transferencia_id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{t.fecha}</td>
                  <td className="py-2 pr-3">{t.origen_alias ?? t.origen_numero}</td>
                  <td className="py-2 pr-3 text-muted-foreground"><ArrowRight className="w-4 h-4" /></td>
                  <td className="py-2 pr-3">{t.destino_alias ?? t.destino_numero ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">
                    {money(t.monto_origen, t.origen_moneda)}
                    {t.destino_moneda && t.destino_moneda !== t.origen_moneda && t.monto_destino != null && (
                      <span className="text-muted-foreground"> → {money(t.monto_destino, t.destino_moneda)}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">{t.concepto ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{t.referencia ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Nueva transferencia" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Cuenta origen *">
              <select className={fieldCls} value={form.origen} onChange={(e) => set({ origen: e.target.value })}>
                <option value="">— Selecciona —</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}
              </select>
            </Campo>
            <Campo label="Cuenta destino *">
              <select className={fieldCls} value={form.destino} onChange={(e) => set({ destino: e.target.value })}>
                <option value="">— Selecciona —</option>
                {cuentas.filter((c) => c.id !== form.origen).map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label={`Monto${mOrigen ? ` (${mOrigen})` : ''} *`}><input type="number" step="0.01" min="0" className={fieldCls} value={form.monto} onChange={(e) => set({ monto: Number(e.target.value) })} /></Campo>
              <Campo label="Fecha *"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => set({ fecha: e.target.value })} /></Campo>
            </div>
            {distintaMoneda && (
              <Campo label={`Monto acreditado en destino (${mDestino}) *`}>
                <input type="number" step="0.01" min="0" className={fieldCls} value={form.monto_destino} onChange={(e) => set({ monto_destino: Number(e.target.value) })} />
                <span className="text-xs text-muted-foreground">Monedas distintas: indica el monto que llega a la cuenta destino.</span>
              </Campo>
            )}
            <Campo label="Concepto"><input className={fieldCls} value={form.concepto} onChange={(e) => set({ concepto: e.target.value })} /></Campo>
            <Campo label="Referencia"><input className={fieldCls} value={form.referencia} onChange={(e) => set({ referencia: e.target.value })} /></Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={transferir.isPending}
                disabled={!form.origen || !form.destino || form.origen === form.destino || Number(form.monto) <= 0 || (distintaMoneda && Number(form.monto_destino) <= 0)}
                onClick={() => transferir.mutate()}>Transferir</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
